import { Injectable, computed, inject, signal } from '@angular/core';
import { Permission, EmployerRole, UserContext } from '@typings/app/rbac.types';
import { ROLE_PERMISSIONS } from './rbac.config';
import { CompanySessionService } from '@core/auth/company-session.service';

@Injectable({ providedIn: 'root' })
export class RbacService {
  private readonly companySession = inject(CompanySessionService);

  /** Explicit override; when null the context is derived from the active membership. */
  private readonly _override = signal<UserContext | null>(null);

  /**
   * Derived synchronously from the active membership so route guards see correct
   * permissions immediately on bootstrap (e.g. a hard refresh on /hr/locations),
   * without waiting for an effect to flush.
   */
  private readonly _userContext = computed<UserContext | null>(() => {
    const override = this._override();
    if (override) return override;

    const membership = this.companySession.activeMembership();
    return membership ? { roles: [membership.role], permissions: [] } : null;
  });

  setUserContext(context: UserContext): void {
    this._override.set(context);
  }

  clearUserContext(): void {
    this._override.set(null);
  }

  permissions = computed<Permission[]>(() => {
    const ctx = this._userContext();
    if (!ctx) return [];

    const fromRoles = ctx.roles.flatMap((role: EmployerRole) => ROLE_PERMISSIONS[role] ?? []);
    const explicit = ctx.permissions ?? [];
    return Array.from(new Set([...fromRoles, ...explicit]));
  });

  roles = computed<EmployerRole[]>(() => {
    const ctx = this._userContext();
    return ctx?.roles ?? [];
  });

  hasPermission(perm: Permission) {
    return computed(() => this.permissions().includes(perm));
  }

  hasAnyPermission(perms: Permission[]) {
    return computed(() => perms.some((p) => this.permissions().includes(p)));
  }

  hasLocationAccess(locId: string) {
    return computed(() => {
      const ctx = this._userContext();
      if (!ctx?.workLocationIds) return false;
      return ctx.workLocationIds.includes(locId);
    });
  }
}
